// Pure helpers for the admin Fans tab (components/AdminFansTab.tsx): the list
// URL, how a fan and an order read, and the CSV export. No React, no network
// of their own — covered in AdminFansTab.test.tsx.
import type { AdminFanList, AdminFanOrder, AdminFanRow, FanAppFilter } from "../../types/admin";
import { byCurrencyPriority, formatTxnAmount } from "../../utils/currency";

export const PAGE_SIZE = 50;
// The CSV pages through the list at the server's largest page, up to a cap.
export const CSV_PAGE_SIZE = 200;
export const CSV_MAX_ROWS = 10000;

export function fansUrl({ q, app, limit, skip }: { q: string; app: FanAppFilter; limit: number; skip: number }): string {
  const params = new URLSearchParams();
  if (q) params.set("q", q);
  params.set("app", app);
  params.set("limit", String(limit));
  params.set("skip", String(skip));
  return `/fans?${params.toString()}`;
}

export const PROVIDER_LABEL: Record<AdminFanRow["provider"], string> = {
  google: "Google",
  apple: "Apple",
  email: "Email link",
};

// "ML" for "Maya Laurent"; a fan with no name yet gets the design's "—".
export function initials(name: string | null): string {
  const words = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "—";
  return words
    .slice(0, 2)
    .map((w) => Array.from(w)[0].toUpperCase())
    .join("");
}

// The apps this fan has used, as the badges read. Empty = web only.
export function appLabels(platforms: string[]): string[] {
  return [platforms.includes("ios") && "iPhone", platforms.includes("android") && "Android"].filter(
    (l): l is string => !!l
  );
}

// The line under a fan's name. Apple can withhold the email address.
export function contactLine(fan: Pick<AdminFanRow, "email" | "instagram" | "provider">): string {
  const email = fan.email || (fan.provider === "apple" ? "Apple hid email" : "No email");
  return fan.instagram ? `${email} · @${fan.instagram}` : email;
}

// "Aug 12" this year, "Aug 12, 2025" before it.
export function shortDate(iso: string | null, now = new Date()): string {
  const d = new Date(iso || "");
  if (isNaN(d.getTime())) return "";
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", ...(sameYear ? {} : { year: "numeric" }) });
}

// "Just now", "3h ago", "Yesterday", then the date.
export function whenText(iso: string, now = new Date()): string {
  const d = new Date(iso);
  const ms = now.getTime() - d.getTime();
  if (ms < 60 * 60 * 1000) return "Just now";
  if (d.toDateString() === now.toDateString()) return `${Math.floor(ms / 3600000)}h ago`;
  if (d.toDateString() === new Date(now.getTime() - 24 * 60 * 60 * 1000).toDateString()) return "Yesterday";
  return shortDate(iso, now);
}

// "Signed in with Google 62% · Apple 30% · Email link 8%" — biggest first,
// methods nobody uses left out; null when there are no accounts yet.
export function signInShareLine(providers: Partial<Record<AdminFanRow["provider"], number>> | undefined): string | null {
  const entries = (Object.entries(providers || {}) as [AdminFanRow["provider"], number][]).filter(([, n]) => n > 0);
  const total = entries.reduce((s, [, n]) => s + n, 0);
  if (!total) return null;
  const parts = entries
    .sort((a, b) => b[1] - a[1])
    .map(([p, n]) => `${PROVIDER_LABEL[p]} ${Math.round((n / total) * 100)}%`);
  return `Signed in with ${parts.join(" · ")}`;
}

// Why a refund can't happen, in the admin's words (server reasons from
// lib/djRefund.js refundEligibility + checkRefundBalanceGuard).
export function refundBlockedText(reason: string): string {
  switch (reason) {
    case "already_refunded":
      return "Already refunded.";
    case "window_expired":
      return "Played more than 30 days ago, so it can't be refunded here.";
    case "already_cashed_out":
    case "balance_short":
    case "no_stripe_account":
      return "The talent already cashed this out, so a refund would cost Request App the money.";
    case "balance_lookup_failed":
      return "Couldn't check the talent's balance. Try again in a minute.";
    case "disputed":
      return "This payment is disputed, so the bank decides it now.";
    default:
      return "This one can't be refunded here.";
  }
}

// The server's spent rule (lib/admin/fans.js KEPT): paid, played, captured,
// never disputed. Anything else shows its amount greyed out.
export const keptMoney = (o: AdminFanOrder): boolean =>
  o.paid && o.status === "played" && !o.captureFailed && !o.isDisputed;

// A request the Refund button is offered on: played and kept, as above. The
// server decides the rest — the 30-day window, a cashed-out talent, the
// talent's balance — and the confirm sheet says why.
export const refundOffered = (o: AdminFanOrder): boolean => keptMoney(o);

export function orderTitle(o: AdminFanOrder): string {
  if (o.isTip) return "Tip";
  return o.searchQuery || (o.isPlaylist ? "Playlist" : "Request");
}

// The order's state in a word or two; money problems win over the status. A
// tip is stored as "played" from the start, so it reads "paid".
export function orderStatusText(o: AdminFanOrder): string {
  if (!o.paid) return "not paid";
  if (o.isDisputed) return "disputed";
  if (o.captureFailed) return "payment didn't clear";
  if (o.isTip && o.status === "played") return "paid";
  return o.status;
}

// What the fan's card was charged (or held for): the card total, else the price.
export function orderAmountText(o: AdminFanOrder): string {
  const amount = o.totalAmount ?? o.amount;
  return amount > 0 ? formatTxnAmount(amount, o.currency) : "Free";
}

/* ---------- CSV ---------- */

// A text cell a spreadsheet would run as a formula (= + - @) is prefixed
// with ' — names, emails and handles are typed by fans.
export function csvCell(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "number") return String(v);
  const safe = /^[=+\-@\t\r]/.test(v) ? `'${v}` : v;
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

// A date column: YYYY-MM-DD (UTC), blank when there is none.
function day(iso: string | null): string {
  const d = new Date(iso || "");
  return isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
}

// One row per fan; spent as one column per currency present (display
// priority), plain numbers so a sheet can add them up.
export function buildFansCsv(rows: AdminFanRow[]): string {
  const currencies = Array.from(new Set(rows.flatMap((r) => Object.keys(r.spentByCurrency)))).sort(byCurrencyPriority);
  const header = [
    "Fan id", "Name", "Email", "Instagram", "Signed in with", "App", "Last on the app",
    "Requests", "Played", "Refunded", "Tips",
    ...currencies.map((c) => `Spent ${c.toUpperCase()}`),
    "Talent supported", "Last request", "Joined",
  ];
  // The handle goes in bare: a cell starting with @ reads as a formula.
  const lines = rows.map((r) => [
    r._id, r.name, r.email, r.instagram, PROVIDER_LABEL[r.provider],
    appLabels(r.appPlatforms).join(" + ") || "Web", day(r.appLastSeenAt),
    r.requests, r.played, r.refunded, r.tips,
    ...currencies.map((c) => r.spentByCurrency[c] ?? 0),
    r.talentCount, day(r.lastRequestAt), day(r.createdAt),
  ]);
  return [header, ...lines].map((cells) => cells.map(csvCell).join(",")).join("\r\n") + "\r\n";
}

// Every fan in the current search + chip, page by page, up to the cap. A
// fan who signs up mid-export shifts the pages, so rows are kept once by id;
// an empty page, or walking past the total, ends it.
export async function fetchAllFans(
  fetchPage: (skip: number, limit: number) => Promise<AdminFanList>
): Promise<{ rows: AdminFanRow[]; total: number }> {
  const byId = new Map<string, AdminFanRow>();
  let total = Infinity;
  let skip = 0;
  while (byId.size < Math.min(total, CSV_MAX_ROWS) && skip < total) {
    const page = await fetchPage(skip, CSV_PAGE_SIZE);
    total = page.total;
    if (!page.items.length) break;
    for (const r of page.items) if (byId.size < CSV_MAX_ROWS) byId.set(r._id, r);
    skip += page.items.length;
  }
  return { rows: Array.from(byId.values()), total };
}
